import {Injectable} from '@angular/core';
import {ModalController} from '@ionic/angular';
import {from, noop, Observable, of, throwError} from 'rxjs';
import {ModalOptions} from '@ionic/core';
import {map, switchMap, tap} from 'rxjs/operators';
import {v4 as uuidv4} from 'uuid';

@Injectable({
    providedIn: 'root'
})
export class ModalControllerService {
    constructor(private modalController: ModalController) {
    }

    /**
     * Display a component within a modal (which is not tied to a particular route and goes fullscreen, hiding the menu).
     *
     * This method returns an observable, containing an unique identifier of the modal, which completes when modal is displayed.
     * @param modalOptions Contains modal options such as the component to be displayed and its input properties
     * @see displayAndWaitForCloseOutput
     */
    display(modalOptions: ModalOptions): Observable<string> {
        if (!modalOptions.id) {
            modalOptions.id = uuidv4();
        }

        return from(this.modalController.create(modalOptions))
            .pipe(
                tap(modal => modal.present()),
                map(() => modalOptions.id)
            );
    }

    /**
     * Display a component within a modal (which is not tied to a particular route and goes fullscreen, hiding the menu).
     *
     * Unlike {@link display}, this method returns an observable, that could carry some output data, which completes when modal is closed.
     * Therefore, if you're not interested in any output when the modal is being closed then you should use {@link display} method.
     * @param modalOptions Contains modal options such as the component to be displayed and its input properties
     * @see display
     */
    displayAndWaitForCloseOutput<T>(modalOptions: ModalOptions): Observable<T | never> {
        return from(this.modalController.create(modalOptions))
            .pipe(
                tap(modal => modal.present()),
                switchMap(modal => from(modal.onWillDismiss()) as Observable<any>),
                switchMap(event => {
                    if (event.data instanceof Error) {
                        return throwError(() => event.data);
                    } else {
                        return of(event.data as T);
                    }
                })
            );
    }

    /**
     * Close a specific modal by its id
     * @param id Identifier of the modal to close.
     * @see close
     */
    closeById(id: string): Observable<void> {
        return this.close(undefined, id);
    }

    /**
     * Close a modal
     * @param output Any output data to pass back to component who displayed the modal
     * @param id Optional identifier of the modal to close. The top overlay is closed when id is not provided.
     * @see closeById
     */
    close<T>(output?: T, id?: string): Observable<void> {
        return from(this.modalController.dismiss(output, undefined, id))
            .pipe(map(() => noop()));
    }

    /**
     * Close a modal with error
     * @param error Error to pass back to component who displayed the modal
     * @param id Optional identifier of the modal to close. The top overlay is closed when id is not provided.
     * @see closeById
     */
    closeOnError<T>(error?: Error, id?: string): Observable<void> {
        return this.close<Error>(error, id);
    }
}
